/**
 * This collection represents attribute data for n assets
 * Each model has an asset id and an array of attribute datum for that particular asset
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/assetAttributeData';

import {isEmptyValue} from '@netx/core/utils/isEmptyValue';
import {unescapeAttributeName} from '@netx/core/utils/unescapeAttributeName';

export default class AssetAttributeDatum extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getAssetAttributes: 'Loading asset attributes',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getAssetAttributes: 'Loaded asset attributes',
		};
	}

	/**
	 * @property {object} methods                     - Methods for this collection
	 * @property {method} methods.read                - Fetch asset attribute datum
	 * @property {method} methods.setAssetAttributes  - Update asset attributes datum
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {array} assetIds Asset ids
			 * @param {object} opts Request options
			 */
			read(opts) {
				return ['getAssetAttributes', this.assetIds];
			},
			/**
			 * @method
			 * @param {array} data Attribute datum
			 * @param {object} opts Request options
			 */
			setAssetAttributes(data, opts) {
				return ['setAssetAttributes', data];
			},
		};
	}

	/**
	 * @property {object} parsers                     - Parsers for this collection
	 * @property {method} partser.read                - Parse response of fetch
	 * @property {method} partser.setAssetAttributes  - Parse response of set asset attributes
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} data Attribute datum
			 * @param {object} opts Request options
			 */
			read(data, opts) {
				// Provide default values
				if (this.useDefaultValues) {
					var assetAttributes = this.assetAttributes || Netx.attributes,
						attrModel,
						values;

					data = _.map(data, (d) => {
						return _.extend(d, {
							attributes: _.map(d.attributes, (attr, idx, all) => {
								values = attr.values;
								if (isEmptyValue(values)) {
									attrModel = assetAttributes.get(attr.templateId);
									values = [
										attrModel && attrModel.get('value') != null
											? attrModel.get('value')
											: '',
									];
								}
								return _.extend(attr, {
									values: values,
								});
							}),
						});
					});
				}

				return data;
			},
			/**
			 * @function
			 * @param {string} uuid UUID
			 * @param {array} data Attribute datum
			 * @param {object} opts Request options
			 * @fires module:app#app:pollBatchJob
			 */
			setAssetAttributes(uuid, data, opts) {
				if (!uuid) return false;

				// On save, wipe out all model changes
				this.clearChanges();

				// Poll
				return this.trackBatchJob(uuid, [data], 1000);
			},
		};
	}

	/**
	 * @override
	 * @param {array} models An array of models or model attributes
	 * @param {object} opts Collection options
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * @memberof module:asset/models/assetAttributeDatum
		 * @memberof
		 * @alias assetIds
		 * @type {array}
		 */
		this.assetIds = opts.assetIds || [];

		/**
		 * @memberof module:asset/models/assetAttributeDatum
		 * @alias assetAttributes
		 * @type {Netx.Collections.AssetAttributes}
		 */
		this.assetAttributes = opts.assetAttributes;

		/**
		 * Use default value if no values are present
		 * @type {boolean}
		 */
		this.useDefaultValues = opts.useDefaultValues === true;

		// Hey, listen!
		this
			// On sync, wipe out all model changes
			.listenTo(this, 'sync', this.clearChanges)
			.listenTo(this, 'complete', (self, batchJob, data) => {
				dispatcher.trigger(
					'asset:attributes:submit:succeeded',
					self,
					batchJob,
					data,
				);
			});
	}

	/**
	 * Clear value changes
	 * @method
	 */
	clearChanges() {
		this.invoke('clearChanges');
	}

	/**
	 * @method
	 * @param {number} tempateId Attribute template id
	 * @param {string} name Template name
	 * @param {number} assetId Asset id
	 * @return {array|Netx.Models.AttributeData} Attribute(s)
	 */
	getAttribute(templateId, name, assetId) {
		if (assetId == null) {
			return _.compact(this.map((m) => m.getAttribute(templateId, name)));
		}

		const assetAttributeDataModel = this.get(assetId);
		if (assetAttributeDataModel) {
			return assetAttributeDataModel.getAttribute(templateId, name);
		}
	}

	/**
	 * @method
	 * @param {number|string} tempateId Attribute template id (will support name as well until totally phased out)
	 * @return {array|module:asset/models/assetAttribute} Attribute template(s)
	 */
	getAttributeTemplate(templateId, assetId) {
		const attributeDataModel = this.getAttribute(templateId, assetId);
		if (assetId == null) {
			return _.compact(
				_.map(attributeDataModel, (attrDataModel) =>
					attrDataModel.getTemplate(),
				),
			);
		}

		return attributeDataModel && attributeDataModel.getTemplate();
	}

	/**
	 * @method
	 * @return {array}
	 */
	getChanges() {
		return _.compact(this.map((m) => m.getChanges()));
	}

	/**
	 * If no asset id is passed, all records will be returned
	 * @method
	 * @param {number} assetId Asset id
	 * @return {array}
	 */
	getWritableAttributes(assetId) {
		if (assetId == null) {
			return _.compact(this.map((m) => m.getWritable()));
		}

		const assetAttributeDataModel = this.get(assetId);
		if (assetAttributeDataModel) {
			return assetAttributeDataModel.getWrtiable();
		}
	}

	/**
	 * If no asset id is passed, all records will be returned
	 * @method
	 * @param {number} assetId Asset id
	 * @return {array}
	 */
	getReadOnlyAttributes(assetId) {
		if (assetId == null) {
			return _.compact(this.map((m) => m.getReadOnly()));
		}

		const assetAttributeDataModel = this.get(assetId);
		if (assetAttributeDataModel) {
			return assetAttributeDataModel.getReadOnly();
		}
	}

	/**
	 * If no asset id is passed, all records will be returned
	 * @method
	 * @param {number} assetId Asset id
	 * @return {array}
	 */
	getValues(assetId) {
		if (assetId == null) {
			return _.compact(this.map((m) => m.getValues()));
		}

		const assetAttributeDataModel = this.get(assetId);
		if (assetAttributeDataModel) {
			return assetAttributeDataModel.getValues();
		}
	}

	/**
	 * @method
	 * @param {number} templateId Attribute template id
	 * @param {number} assetId Asset id
	 * @return {array|mixed} Value(s)
	 */
	getValue(templateId, assetId) {
		if (assetId == null) {
			return _.filter(
				this.map((m) => m.getValue(templateId)),
				(val) => val != null,
			);
		}

		const attributeDataModel = this.getAttribute(templateId, null, assetId);
		if (attributeDataModel) {
			return attributeDataModel.getValue(templateId);
		}
	}

	/**
	 * If no asset id is passed, all records will be checked
	 * @method
	 * @param {number} assetId Asset id
	 * @return {boolean}
	 */
	hasAttributeValues(assetId) {
		const values = this.getValues(assetId);

		for (let i = 0, c = values.length, foundValue, _values; i < c; i++) {
			_values = values[i];
			for (let j = 0, k = _values.length, val; j < k; j++) {
				val = _values[j];
				if (_.isArray(val)) {
					foundValue = val.length > 0 && val !== '' && val != null;
				} else {
					foundValue = val !== '' && val != null;
				}
				if (foundValue) {
					return true;
				}
			}
		}
		return false;
	}

	/**
	 * If no asset id is passed, all records will be checked
	 * @method
	 * @param {number} assetId Asset id
	 * @return {boolean}
	 */
	hasWritableAttributes(assetId) {
		const attributes = this.getWritableAttributes(assetId);
		for (let i = 0, c = attributes.length; i < c; i++) {
			if (attributes[0].length > 0) {
				return true;
			}
		}
		return false;
	}

	/**
	 * @method
	 * @param {object} data Attribute data
	 * @param {number} assetId Asset id (if not provided, data will be applied to all assets in this collection)
	 * @param {object} opts Options
	 */
	parseAndSave(data, assetId, opts) {
		_.each(data, (v, k) => {
			// This most likely came from a form, need to be safe and unescape the name
			// TODO: look into switching our attribute names in forms to utilize the templateId instead
			// User defined names as form input names is troublesome
			k = unescapeAttributeName(k);

			const attrModel = (this.assetAttributes || Netx.attributes).findWhere({
				name: k,
			});
			if (attrModel && attrModel.isValueWritable()) {
				v = attrModel.parseOutValue(v);
				// Set - default to silently as this will most likely be the product of cloned data and a form update
				// so the underlying attribute data model does not need to respond to the change and populate the values collection
				// as the values collection should already be mirrored in the UI
				this.setValue(attrModel.id, assetId, v, _.extend({silent: true}, opts));
			}
		});

		// Save
		return this.setAssetAttributes(this.getChanges(), opts);
	}

	/**
	 * Reset all values
	 * @method
	 * @param {object} opts Options
	 * @see Netx.Collections.AttributeDatum
	 */
	resetValues(opts) {
		return this.invoke('resetValues', opts);
	}

	/**
	 * @method
	 * @param {number} templateId Attribute template id
	 * @param {array|mixed} val Value(s)
	 * @param {object} opts Options
	 */
	setValue(templateId, assetId, val, opts) {
		if (assetId == null) {
			this.invoke('setValue', templateId, val, opts);
			return;
		}

		const attributeDataModel = this.getAttribute(templateId, null, assetId);
		attributeDataModel && attributeDataModel.setValue(templateId, val, opts);
	}

	/** @type {string} */
	static get __name__() {
		return 'AssetAttributeDatum';
	}
}
